import type { ImportEntitiy, ImporterConfig } from '@/engine/Serialization/IImporter';
import { buildAlgorithm } from './TAVRAlgorithmBuilder';

export function getConfig(): ImporterConfig {
  const meshes: Array<ImportEntitiy> = [
    // Main step meshes
    {
      importName: 'left ventricle',
      algoName: 'left_ventricle.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aorta',
      algoName: 'aorta.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aorta_lv',
      algoName: 'aorta_lv.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'left heart',
      algoName: 'left_heart.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'rcc calcification',
      algoName: 'rcc_calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'lcc calcification',
      algoName: 'lcc_calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ncc calcification',
      algoName: 'ncc_calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'additional calcification',
      algoName: 'additional_calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'additional device',
      algoName: 'additional_device.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Heart'
    },
    // Delivery step meshes
    {
      importName: 'artery',
      algoName: 'vessel.descriptor',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'heart',
      algoName: 'heart.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'aneurysm',
      algoName: 'aneurysm.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'additional structure',
      algoName: 'vessel_calcification.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'rib cage',
      algoName: 'rib_cage.descriptor',
      isOptionalEntity: true,
      imagePhaseKind: 'Body'
    }
  ];

  const points: Array<ImportEntitiy> = [
    // Main step points
    {
      importName: 'rcc',
      algoName: 'tp.pt_rcc.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'lcc',
      algoName: 'tp.pt_lcc.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ncc',
      algoName: 'tp.pt_ncc.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'rc',
      algoName: 'tp.pt_rc.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'lc',
      algoName: 'tp.pt_lc.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'ms',
      algoName: 'tp.pt_ms.dflt',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'mvc',
      algoName: 'pt_mvc_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_rcc',
      algoName: 'pt_sov_rcc_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_rcc_2',
      algoName: 'pt_sov_rcc_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_lcc',
      algoName: 'pt_sov_lcc_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_lcc_2',
      algoName: 'pt_sov_lcc_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_ncc',
      algoName: 'pt_sov_ncc_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'sov_ncc_2',
      algoName: 'pt_sov_ncc_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'stj1',
      algoName: 'pt_stj_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'stj2',
      algoName: 'pt_stj_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'stj3',
      algoName: 'pt_stj_3_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aa1',
      algoName: 'pt_aa_1_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aa2',
      algoName: 'pt_aa_2_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    {
      importName: 'aa3',
      algoName: 'pt_aa_3_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Heart'
    },
    // Delivery step points
    {
      importName: 'rfa',
      algoName: 'pt_right_femoral_vessel_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'lfa',
      algoName: 'pt_left_femoral_vessel_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    },
    {
      importName: 'clr',
      algoName: 'pt_centerline_reference_imported',
      isOptionalEntity: false,
      imagePhaseKind: 'Body'
    }
  ];

  return {
    meshes: meshes,
    points: points,
    splines: [],
    polylinesVectors: [],
    algorithmBuilder: buildAlgorithm,
    optionalImagePhaseKinds: ['Body']
  };
}
